import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';
import {
  invokeDashboardDeferredReadModel,
  invokeDashboardSummaryReadModel,
  invokeInsightsReadModel,
  invokePoamReadModel,
  invokeSuppliersReadModel,
} from '@/lib/readModels';
import { DENSE_PAGE_DEFAULT_PAGE_SIZE } from '@/lib/paginationStandards';

/**
 * Hook to prefetch Insights data on hover/focus for instant navigation.
 * Uses React Query's prefetchQuery to warm the cache before navigation.
 */
export function useInsightsPrefetch() {
  const queryClient = useQueryClient();
  const { organization, user } = useOrganization();

  const prefetchSilently = useCallback((promise: Promise<unknown>) => {
    void promise.catch(() => {
      // Ignore background prefetch failures. Route-level loaders surface real errors.
    });
  }, []);

  const prefetchInsights = useCallback(() => {
    if (!organization?.id) return;

    prefetchSilently(queryClient.prefetchQuery({
      queryKey: ['insights-overview-read-model', organization.id],
      queryFn: () => invokeInsightsReadModel({
        organizationId: organization.id,
        tierFilter: 'all',
        typeFilter: 'all',
        statusFilter: 'active',
        departmentFilter: 'all',
        dateRange: '6m',
        section: 'overview',
      }),
      staleTime: 2 * 60 * 1000, // Consider fresh for 2 minutes
      // ENT-3312: invokeInsightsReadModel now retries transient transport failures
      // internally (mirroring its sibling read models). The global QueryClient
      // policy (queryRetryPolicy) ALSO retries transient errors, so without this a
      // persistent 502/503 would multiply into ~16 Edge Function calls on this
      // background prefetch. The lib owns the retry; keep this path single-layered.
      retry: false,
    }));
  }, [prefetchSilently, queryClient, organization?.id]);

  const prefetchDashboard = useCallback(() => {
    if (!organization?.id) return;

    prefetchSilently(queryClient.prefetchQuery({
      queryKey: ['dashboard-summary-read-model', organization.id],
      queryFn: () => invokeDashboardSummaryReadModel({
        organizationId: organization.id,
      }, {
        expectedUserId: user?.id,
      }),
      staleTime: 2 * 60 * 1000,
    }));
    prefetchSilently(queryClient.prefetchQuery({
      queryKey: ['dashboard-deferred-read-model', organization.id],
      queryFn: () => invokeDashboardDeferredReadModel({
        organizationId: organization.id,
      }, {
        expectedUserId: user?.id,
      }),
      staleTime: 2 * 60 * 1000,
    }));
  }, [prefetchSilently, queryClient, organization?.id, user?.id]);

  const prefetchSuppliers = useCallback(() => {
    if (!organization?.id) return;

    prefetchSilently(queryClient.prefetchQuery({
      queryKey: ['suppliers-read-model', organization.id],
      queryFn: () => invokeSuppliersReadModel({
        organizationId: organization.id,
        page: 1,
        pageSize: DENSE_PAGE_DEFAULT_PAGE_SIZE,
        searchQuery: '',
        nameSortDir: 'asc',
        departmentFilter: 'all',
        selectedCategory: null,
        filters: {
          criticality: 'all',
          status: 'active',
          questionnaireAccess: false,
          hasCertificates: false,
          supplierType: 'all',
        },
      }),
      staleTime: 60 * 1000,
    }));
  }, [prefetchSilently, queryClient, organization?.id]);

  const prefetchPoam = useCallback(() => {
    if (!organization?.id) return;

    prefetchSilently(queryClient.prefetchQuery({
      queryKey: ['poam-read-model', organization.id],
      queryFn: () => invokePoamReadModel({
        organizationId: organization.id,
        page: 1,
        pageSize: DENSE_PAGE_DEFAULT_PAGE_SIZE,
      }),
      staleTime: 30 * 1000,
    }));
  }, [prefetchSilently, queryClient, organization?.id]);

  return {
    prefetchInsights,
    prefetchDashboard,
    prefetchSuppliers,
    prefetchPoam,
  };
}
